import React, { useState } from 'react';
import {
  FaArrowLeft,
  FaCheckCircle,
  FaExclamationTriangle,
  FaEye,
  FaEyeSlash,
  FaLock,
  FaSignInAlt,
} from 'react-icons/fa';
import { signInWithHostedUi } from '../api/auth';
import {
  signIn,
  completeNewPassword,
  submitMfaCode,
  requestPasswordReset,
  confirmPasswordReset,
  isCustomAuthConfigured,
} from '../api/cognitoAuth';
import AuthLayout from '../components/AuthLayout';
import { InlineSpinner } from '../components/LoadingState';

const PasswordField = ({ label, value, onChange, autoComplete, autoFocus }) => {
  const [visible, setVisible] = useState(false);
  return (
    <div>
      <label className="block text-sm font-semibold text-gray-800 mb-1.5">{label}</label>
      <div className="relative">
        <input
          type={visible ? 'text' : 'password'}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          autoComplete={autoComplete}
          autoFocus={autoFocus}
          className="input-field pr-11"
          required
        />
        <button
          type="button"
          onClick={() => setVisible((v) => !v)}
          aria-label={visible ? 'Hide password' : 'Show password'}
          className="absolute inset-y-0 right-0 flex items-center px-3.5 text-gray-400 hover:text-gray-600"
        >
          {visible ? <FaEyeSlash /> : <FaEye />}
        </button>
      </div>
    </div>
  );
};

const Alert = ({ tone = 'error', title, children }) => {
  const error = tone === 'error';
  return (
    <div
      role="alert"
      className={`flex gap-3 rounded-lg border p-3.5 ${
        error ? 'border-red-200 bg-red-50' : 'border-green-200 bg-green-50'
      }`}
    >
      {error ? (
        <FaExclamationTriangle className="mt-0.5 flex-shrink-0 text-red-600" />
      ) : (
        <FaCheckCircle className="mt-0.5 flex-shrink-0 text-green-600" />
      )}
      <div className="min-w-0">
        <p className={`text-sm font-semibold ${error ? 'text-red-700' : 'text-green-700'}`}>
          {title}
        </p>
        {children && (
          <p className={`mt-0.5 text-sm break-words ${error ? 'text-red-700' : 'text-green-700'}`}>
            {children}
          </p>
        )}
      </div>
    </div>
  );
};

/*
 * Credentials are collected here rather than on the Cognito Hosted UI. The
 * screen is a small state machine over the challenges Cognito can return:
 * signIn -> newPassword | mfa -> done, plus the forgot-password detour.
 */
function LoginPage({ error: initialError }) {
  const [stage, setStage] = useState('signIn');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(initialError || null);
  const [notice, setNotice] = useState(null);
  const [challenge, setChallenge] = useState(null);

  const configured = isCustomAuthConfigured();

  const done = () => {
    const returnPath = sessionStorage.getItem('auth_return_path');
    sessionStorage.removeItem('auth_return_path');
    window.location.replace(returnPath || '/');
  };

  const handle = (result) => {
    if (result.status === 'SUCCESS') {
      done();
      return;
    }
    setChallenge(result);
    setStage(result.status === 'NEW_PASSWORD_REQUIRED' ? 'newPassword' : 'mfa');
  };

  const run = async (fn) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const onSignIn = (e) => {
    e.preventDefault();
    run(async () => {
      const user = username.trim();
      try {
        handle(await signIn(user, password));
      } catch (err) {
        // An admin-triggered "Reset required" account can never sign in
        // again — Cognito rejects every password for it. The only way out
        // is the forgot-password flow, so send the code immediately instead
        // of stranding the user on a form that will never succeed.
        if (err.code === 'PasswordResetRequiredException') {
          await requestPasswordReset(user);
          setNotice('Your password needs to be reset. We sent a reset code to the contact details on your account.');
          setStage('forgotConfirm');
          return;
        }
        throw err;
      }
    });
  };

  const onNewPassword = (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      setError('Those passwords do not match.');
      return;
    }
    run(async () =>
      handle(await completeNewPassword(challenge.user, newPassword, challenge.requiredAttributes))
    );
  };

  const onMfa = (e) => {
    e.preventDefault();
    run(async () =>
      handle(await submitMfaCode(challenge.user, code.trim(), challenge.challengeName))
    );
  };

  const onForgotRequest = (e) => {
    e.preventDefault();
    run(async () => {
      await requestPasswordReset(username.trim());
      setNotice('We sent a reset code to the contact details on your account.');
      setStage('forgotConfirm');
    });
  };

  const onForgotConfirm = (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      setError('Those passwords do not match.');
      return;
    }
    run(async () => {
      await confirmPasswordReset(username.trim(), code.trim(), newPassword);
      setNotice('Password updated. Sign in with your new password.');
      setPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setCode('');
      setStage('signIn');
    });
  };

  const back = (to) => (
    <button
      type="button"
      onClick={() => {
        setStage(to);
        setError(null);
        setNotice(null);
      }}
      className="mt-4 inline-flex items-center gap-2 text-sm font-medium text-gray-500 hover:text-gray-700"
    >
      <FaArrowLeft className="text-xs" />
      Back to sign in
    </button>
  );

  const submit = (label, icon) => (
    <button type="submit" disabled={busy} className="btn-primary btn-lg mt-6 w-full">
      {busy ? (
        <>
          <InlineSpinner light />
          Please wait…
        </>
      ) : (
        <>
          {icon}
          {label}
        </>
      )}
    </button>
  );

  // Without a pool id the form cannot run at all, so fall back to the hosted
  // page rather than presenting inputs that can only fail.
  if (!configured) {
    return (
      <AuthLayout>
        <h1 className="text-2xl font-semibold text-gray-900">Sign in</h1>
        <p className="mt-2 text-sm text-gray-500">
          Continue to Post-Call Analytics with your CloudZA account.
        </p>
        {error && <div className="mt-6"><Alert title="Sign-in failed">{error}</Alert></div>}
        <button
          onClick={() => signInWithHostedUi().catch((e) => setError(e.message))}
          className="btn-primary btn-lg mt-6 w-full"
        >
          <FaSignInAlt />
          Sign in
        </button>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      {stage === 'signIn' && (
        <form onSubmit={onSignIn} noValidate>
          <h1 className="text-2xl font-semibold text-gray-900">Sign in</h1>
          <p className="mt-2 text-sm text-gray-500">
            Continue to Post-Call Analytics with your CloudZA account.
          </p>

          <div className="mt-6 space-y-4">
            {notice && <Alert tone="success" title={notice} />}
            {error && <Alert title="Sign-in failed">{error}</Alert>}

            <div>
              <label className="block text-sm font-semibold text-gray-800 mb-1.5">Username</label>
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                autoComplete="username"
                autoFocus
                className="input-field"
                required
              />
            </div>

            <PasswordField
              label="Password"
              value={password}
              onChange={setPassword}
              autoComplete="current-password"
            />
          </div>

          <div className="mt-3 text-right">
            <button
              type="button"
              onClick={() => {
                setStage('forgotRequest');
                setError(null);
                setNotice(null);
              }}
              className="text-sm font-medium text-rewardsco-600 hover:text-rewardsco-700"
            >
              Forgot your password?
            </button>
          </div>

          {submit('Sign in', <FaSignInAlt />)}
        </form>
      )}

      {stage === 'newPassword' && (
        <form onSubmit={onNewPassword} noValidate>
          <h1 className="text-2xl font-semibold text-gray-900">Choose a new password</h1>
          <p className="mt-2 text-sm text-gray-500">
            Your account was created with a temporary password. Set your own to continue.
          </p>

          <div className="mt-6 space-y-4">
            {error && <Alert title="Could not set password">{error}</Alert>}
            <PasswordField
              label="New password"
              value={newPassword}
              onChange={setNewPassword}
              autoComplete="new-password"
              autoFocus
            />
            <PasswordField
              label="Confirm new password"
              value={confirmPassword}
              onChange={setConfirmPassword}
              autoComplete="new-password"
            />
          </div>

          {submit('Set password and continue', <FaLock />)}
        </form>
      )}

      {stage === 'mfa' && (
        <form onSubmit={onMfa} noValidate>
          <h1 className="text-2xl font-semibold text-gray-900">Verification required</h1>
          <p className="mt-2 text-sm text-gray-500">
            Enter the code from your authentication method to finish signing in.
          </p>

          <div className="mt-6 space-y-4">
            {error && <Alert title="Verification failed">{error}</Alert>}
            <div>
              <label className="block text-sm font-semibold text-gray-800 mb-1.5">Code</label>
              <input
                type="text"
                inputMode="numeric"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                autoComplete="one-time-code"
                autoFocus
                className="input-field tracking-widest"
                required
              />
            </div>
          </div>

          {submit('Verify', <FaCheckCircle />)}
        </form>
      )}

      {stage === 'forgotRequest' && (
        <form onSubmit={onForgotRequest} noValidate>
          <h1 className="text-2xl font-semibold text-gray-900">Reset your password</h1>
          <p className="mt-2 text-sm text-gray-500">
            Enter your username and we will send you a reset code.
          </p>

          <div className="mt-6 space-y-4">
            {error && <Alert title="Could not send code">{error}</Alert>}
            <div>
              <label className="block text-sm font-semibold text-gray-800 mb-1.5">Username</label>
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                autoComplete="username"
                autoFocus
                className="input-field"
                required
              />
            </div>
          </div>

          {submit('Send reset code', <FaSignInAlt />)}
          {back('signIn')}
        </form>
      )}

      {stage === 'forgotConfirm' && (
        <form onSubmit={onForgotConfirm} noValidate>
          <h1 className="text-2xl font-semibold text-gray-900">Enter your reset code</h1>
          {notice && <p className="mt-2 text-sm text-gray-500">{notice}</p>}

          <div className="mt-6 space-y-4">
            {error && <Alert title="Could not reset password">{error}</Alert>}
            <div>
              <label className="block text-sm font-semibold text-gray-800 mb-1.5">Reset code</label>
              <input
                type="text"
                inputMode="numeric"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                autoComplete="one-time-code"
                autoFocus
                className="input-field tracking-widest"
                required
              />
            </div>
            <PasswordField
              label="New password"
              value={newPassword}
              onChange={setNewPassword}
              autoComplete="new-password"
            />
            <PasswordField
              label="Confirm new password"
              value={confirmPassword}
              onChange={setConfirmPassword}
              autoComplete="new-password"
            />
          </div>

          {submit('Set new password', <FaLock />)}
          {back('signIn')}
        </form>
      )}

      <p className="mt-8 flex items-center justify-center gap-2 border-t border-gray-100 pt-6 text-xs text-gray-400">
        <FaLock />
        Secured by CloudZA single sign-on
      </p>
    </AuthLayout>
  );
}

export default LoginPage;
